import { useSession } from "@/services/auth";
import { useQuery } from "@tanstack/react-query";
import axios from "axios";
import type {
  EstimateUnavailableReason,
  PickupAvailabilityStatus,
  SlotDeliveryEstimate,
} from "@/api.types.v2";

const API_URL = import.meta.env.VITE_API_URL;

interface SlotAvailabilityResponse {
  data: {
    remaining: Record<string, number | null>;
    statuses: Record<string, PickupAvailabilityStatus>;
    status: PickupAvailabilityStatus;
    nextAvailableDate: string | null;
    /** Present only when estimateCarrierServiceId was sent. */
    estimates?: Record<string, SlotDeliveryEstimate | null>;
    estimateReasons?: Record<string, EstimateUnavailableReason | null>;
  };
}

/**
 * Per-slot remaining capacity for a chosen pickup date.
 *
 * The quote (`get-quote`) returns pickup slots WITHOUT capacity, because it's
 * computed before the customer picks a date. Once a date is chosen in the
 * pricing step, this fetches `remaining` per slot from
 * POST /api/v2/pricing/slot-availability (keyed on slotIds + date only — no
 * rate recompute, no address resolution).
 *
 * Returns a `{ [slotId]: remaining|null }` map. Disabled (no request) until
 * there is at least one slot id and a date.
 */
export function useSlotAvailability(
  slotIds: string[],
  date: string,
  /** When set, the response also carries per-slot delivery estimates
   *  recomputed for `date` in the context of this quoted carrier service. */
  estimateCarrierServiceId?: string
) {
  const { token } = useSession();
  // Stable key so re-renders with the same slots don't refetch.
  const idsKey = [...slotIds].sort().join(",");

  return useQuery({
    queryKey: ["slot-availability", idsKey, date, estimateCarrierServiceId],
    enabled: !!token && slotIds.length > 0 && !!date,
    // Capacity drifts as others book — keep it reasonably fresh.
    staleTime: 30_000,
    queryFn: async () => {
      const { data } = await axios.post<SlotAvailabilityResponse>(
        `${API_URL}/api/v2/pricing/slot-availability`,
        {
          slotIds,
          date,
          ...(estimateCarrierServiceId ? { estimateCarrierServiceId } : {}),
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      return data.data;
    },
  });
}
